import type { StrictOmit } from '@enmon/commons';
import {
  type MDXEditorMethods,
  type MDXEditorProps as OfficialMDXEditorProps,
  MDXEditor as OfficialMDXEditor,
  UndoRedo,
  BoldItalicUnderlineToggles,
  StrikeThroughSupSubToggles,
  ListsToggle,
  BlockTypeSelect,
  InsertThematicBreak,
  listsPlugin,
  headingsPlugin,
  thematicBreakPlugin,
  markdownShortcutPlugin,
  toolbarPlugin,
} from '@mdxeditor/editor';
import '@mdxeditor/editor/style.css';
import { type ForwardedRef, forwardRef, useCallback } from 'react';
import styled from 'styled-components';

const StyledMDXEditor = styled(OfficialMDXEditor)`
  max-height: 300px;
  overflow: auto;
  border: 1px solid #d9d9d9;
  border-radius: 2px;

  .mdxeditor-toolbar {
    border-radius: 0px;
  }
`;

export interface MDXEditorProps extends StrictOmit<OfficialMDXEditorProps, 'markdown'> {
  value?: string;
  onChange?: (value: string) => void;
}

export const MDXEditor = forwardRef(
  ({ value, ...restProps }: MDXEditorProps, ref: ForwardedRef<MDXEditorMethods> | null) => {
    const toolbarContentsRenderer = useCallback(
      () => (
        <>
          <UndoRedo />
          <BoldItalicUnderlineToggles />
          <StrikeThroughSupSubToggles />
          <ListsToggle options={['bullet', 'check']} />
          <BlockTypeSelect />
          <InsertThematicBreak />
        </>
      ),
      [],
    );

    return (
      <StyledMDXEditor
        ref={ref}
        markdown={value ?? ''}
        placeholder="Write something..."
        plugins={[
          listsPlugin(),
          headingsPlugin({ allowedHeadingLevels: [1, 2, 3, 4] }),
          thematicBreakPlugin(),
          markdownShortcutPlugin(),
          toolbarPlugin({
            toolbarContents: toolbarContentsRenderer,
          }),
        ]}
        {...restProps}
      />
    );
  },
);
